export type ProcessHandler<T = object> = (this: T, ...args: any[]) => any;
export type Middleware<T> = (next: ProcessHandler<T>, name: string) => ProcessHandler<T>;
export function middleware<T>(...ms: Middleware<T>[]) {
  return function (target: object, fnName: string, descriptor: PropertyDescriptor) {
    descriptor.value = ms.reduceRight((acc, c) => c.call(target, acc, fnName), descriptor.value);
    return descriptor;
  };
}
export function before<T>(handler: ProcessHandler<T>) {
  return createDecorator((next: ProcessHandler<T>, name: string) => async function (this: T, ...args: any[]) {
    await handler.apply(this, args);
    return next.apply(this, args);
  });
}
export function after<T>(handler: ProcessHandler<T>) {
  return createDecorator((next: ProcessHandler<T>, name: string) => async function (this: T, ...args: any[]) {
    const result = await next.apply(this, args);
    await handler.call(this, ...args);
    return result;
  });
}

export function catchError<T>(handler: ProcessHandler<T>) {
  return createDecorator((next: ProcessHandler<T>, name: string) => async function (this: T, ...args: any[]) {
    try {
      return await next.apply(this, args);
    } catch (error) {
      handler.call(this, error);
    }
  });
}

export function createDecorator<T>(md: Middleware<T>) {
  // target: 装饰器所在类的原型，例如在 Client.join 接口加了参数校验，则 target 为 Client.prototype。
  // fnName: 装饰器修饰的函数名，例如在 Client.join 接口加了参数校验，则 fnName 为 'join'。
  // descriptor: 装饰器修饰函数的属性描述对象，参考：https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperty
  return function (target: object, fnName: string, descriptor: PropertyDescriptor) {
    descriptor.value = md(descriptor.value, fnName);
    return descriptor;
  };
}

export function createDecoratorBind<T>(handler: (next: () => any, name: string) => any) {
  const md = (next: ProcessHandler<T>, name: string) => async function (this: T, ...args: any[]) {
    return handler.call(this, next.bind(this, ...args), name);
  };
  return function (target: object, fnName: string, descriptor: PropertyDescriptor) {
    descriptor.value = md(descriptor.value, fnName);
    return descriptor;
  };
}
